import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type ContactLocationState = {
    message?: unknown;
};

type ContactResponse = {
    ok?: boolean;
    error?: string;
};

export default function ContactPage(): JSX.Element {
    const location = useLocation();
    const navigate = useNavigate();

    const initialMessage = useMemo((): string => {
        const state = location.state as ContactLocationState | null;
        if (!state || typeof state !== "object") return "";
        if (!("message" in state)) return "";
        return typeof state.message === "string"
            ? state.message
            : String(state.message ?? "");
    }, [location.state]);

    const [submitted, setSubmitted] = useState<boolean>(false);
    const [message, setMessage] = useState<string>(initialMessage);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    return (
        <div className="mx-auto max-w-3xl space-y-8">
            <div className="space-y-2">
                <button
                    type="button"
                    onClick={() =>
                        window.history.length > 1 ? navigate(-1) : navigate("/")
                    }
                    className="inline-flex items-center gap-2 rounded-2xl border border-[rgb(var(--border)/0.15)] bg-[rgb(var(--surface)/0.05)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgb(var(--surface)/0.10)]"
                >
                    <span aria-hidden="true">←</span>
                    Back
                </button>

                <h1 className="text-2xl font-semibold tracking-tight">
                    Contact
                </h1>
                <p className="text-sm text-[rgb(var(--muted))]">
                    Send us a message and we’ll get back to you shortly.
                </p>
            </div>

            {submitted ? (
                <div className="rounded-3xl border border-green-500/30 bg-green-500/20 p-6 text-sm text-white">
                    Thank You! Your message has been sent.
                </div>
            ) : (
                <form
                    className="space-y-6 rounded-3xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--surface)/0.05)] p-6"
                    onSubmit={async (e) => {
                        e.preventDefault();

                        if (submitting) return;

                        const form = e.currentTarget;
                        const formData = new FormData(form);

                        const root = getComputedStyle(document.documentElement);
                        const css = (name: string): string =>
                            root.getPropertyValue(name).trim();

                        const payload = {
                            firstName: String(
                                formData.get("firstName") ?? "",
                            ).trim(),
                            lastName: String(
                                formData.get("lastName") ?? "",
                            ).trim(),
                            email: String(formData.get("email") ?? "").trim(),
                            phone: String(formData.get("phone") ?? "").trim(),
                            message: message.trim(),
                            theme: {
                                accent: css("--accent"),
                                accentFg: css("--accent-fg"),
                                fg: css("--fg"),
                                surface: css("--surface"),
                                panel: css("--panel"),
                                border: css("--border"),
                                muted: css("--muted"),
                                muted2: css("--muted2"),
                                muted3: css("--muted3"),
                            },
                        };

                        setSubmitting(true);
                        setError(null);

                        try {
                            const res = await fetch("/api/contact", {
                                method: "POST",
                                mode: "cors", // Explicit for cross-origin behavior
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify(payload),
                            });

                            const data = (await res
                                .json()
                                .catch(() => null)) as ContactResponse | null;

                            if (!res.ok || !data?.ok) {
                                setError(
                                    data?.error ??
                                        `Request failed (${res.status})`,
                                );
                                return;
                            }

                            setSubmitted(true);
                        } catch (err) {
                            setError(
                                err instanceof Error
                                    ? err.message
                                    : String(err),
                            );
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {error ? (
                        <div className="rounded-2xl border border-red-500/30 bg-red-500/20 px-4 py-3 text-sm text-white">
                            {error}
                        </div>
                    ) : null}

                    <div className="grid gap-4 sm:grid-cols-2">
                        <label className="space-y-1">
                            <span className="text-xs font-medium text-[rgb(var(--muted2))]">
                                First name{" "}
                                <span className="text-red-500">*</span>
                            </span>
                            <input
                                required
                                name="firstName"
                                type="text"
                                className="w-full rounded-2xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--panel)/0.50)] px-4 py-3 text-sm text-[rgb(var(--fg))] outline-none transition focus:border-[rgb(var(--border)/0.20)] focus:ring-2 focus:ring-[rgb(var(--accent)/0.18)]"
                            />
                        </label>

                        <label className="space-y-1">
                            <span className="text-xs font-medium text-[rgb(var(--muted2))]">
                                Last name{" "}
                                <span className="text-red-500">*</span>
                            </span>
                            <input
                                required
                                name="lastName"
                                type="text"
                                className="w-full rounded-2xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--panel)/0.50)] px-4 py-3 text-sm text-[rgb(var(--fg))] outline-none transition focus:border-[rgb(var(--border)/0.20)] focus:ring-2 focus:ring-[rgb(var(--accent)/0.18)]"
                            />
                        </label>
                    </div>

                    <label className="space-y-1">
                        <span className="text-xs font-medium text-[rgb(var(--muted2))]">
                            Email <span className="text-red-500">*</span>
                        </span>
                        <input
                            required
                            name="email"
                            type="email"
                            className="w-full rounded-2xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--panel)/0.50)] px-4 py-3 text-sm text-[rgb(var(--fg))] outline-none transition focus:border-[rgb(var(--border)/0.20)] focus:ring-2 focus:ring-[rgb(var(--accent)/0.18)]"
                        />
                    </label>

                    <label className="space-y-1">
                        <span className="text-xs font-medium text-[rgb(var(--muted2))]">
                            Phone (optional)
                        </span>
                        <input
                            name="phone"
                            type="tel"
                            className="w-full rounded-2xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--panel)/0.50)] px-4 py-3 text-sm text-[rgb(var(--fg))] outline-none transition focus:border-[rgb(var(--border)/0.20)] focus:ring-2 focus:ring-[rgb(var(--accent)/0.18)]"
                        />
                    </label>

                    <label className="space-y-1">
                        <span className="text-xs font-medium text-[rgb(var(--muted2))]">
                            Message <span className="text-red-500">*</span>
                        </span>
                        <textarea
                            required
                            rows={6}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className="w-full resize-none rounded-2xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--panel)/0.50)] px-4 py-3 text-sm text-[rgb(var(--fg))] outline-none transition focus:border-[rgb(var(--border)/0.20)] focus:ring-2 focus:ring-[rgb(var(--accent)/0.18)]"
                        />
                    </label>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-xs text-[rgb(var(--muted3))]">
                            By sending this message you agree to be contacted
                            about your inquiry.
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="inline-flex items-center justify-center rounded-2xl bg-[rgb(var(--accent))] px-4 py-3 text-sm font-semibold text-[rgb(var(--accent-fg))] shadow-sm transition hover:opacity-90 disabled:opacity-60"
                        >
                            {submitting ? "Sending..." : "Send message"}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
